'use client'
import type { ReactNode } from 'react'

import { tw } from '@/lib/utils'
import type { NavItemConfig } from '@/types/nav-types'

import { SheetSkeletonCard } from './sheet-skeleton-card'

import { useIsWideLauncher } from '../use-is-wide-launcher'

export type SheetCardSize = 'lead' | 'small'

/** Lead cards across the top of a band list, as the author archive's top row; four on wide screens. */
export const LEAD_CARDS = 3
export const LEAD_CARDS_WIDE = 4

interface SheetCardGridProps {
  items: NavItemConfig[]
  renderItem: (item: NavItemConfig, size: SheetCardSize) => ReactNode
  /** Placeholder cards instead of items, this many. */
  loading?: number
}

const LEAD_COLS = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' } as const

const leadCount = (items: NavItemConfig[], max: number) => Math.min(max, items.length)

// The band's list laid out as the author archive lays out stories: the first
// three (four on wide screens) as lead cards across the row, the rest as small
// cards in two columns (three on wide screens). A lone remainder keeps its
// column width; a small card never runs wide.
export const SheetCardGrid = ({ items, renderItem, loading }: SheetCardGridProps) => {
  const maxLead = useIsWideLauncher() ? LEAD_CARDS_WIDE : LEAD_CARDS
  const count = loading ?? items.length
  const lead = loading ? Math.min(maxLead, loading) : leadCount(items, maxLead)
  const slots = (from: number, to: number) => Array.from({ length: Math.max(0, to - from) }, (_, i) => from + i)
  const cell = (i: number, size: SheetCardSize) =>
    loading ? <SheetSkeletonCard small={size === 'small'} /> : renderItem(items[i], size)
  const key = (i: number) => (loading ? i : items[i].id)

  return (
    <div className="flex flex-col gap-4">
      {lead > 0 ? (
        <ul className={tw('m-0 grid list-none gap-4 p-0', LEAD_COLS[lead as 1 | 2 | 3 | 4])}>
          {slots(0, lead).map((i) => (
            <li key={key(i)}>{cell(i, 'lead')}</li>
          ))}
        </ul>
      ) : null}
      {count > lead ? (
        <ul className="m-0 grid list-none grid-cols-2 gap-4 p-0 desktop-xl:grid-cols-3">
          {slots(lead, count).map((i) => (
            <li key={key(i)}>{cell(i, 'small')}</li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
